import { BarChart3, ChevronDown, Database, MessageCircle, Shield, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { Assignment, Programme, Review, Submission } from "@/types";

const STANDARD_CRITERIA = [
  { id: "research_integrity", name: "Research Integrity", icon: Database },
  { id: "analytical_reasoning", name: "Analytical Reasoning", icon: BarChart3 },
  {
    id: "relevance_and_investment_impact",
    name: "Relevance & Investment Impact",
    icon: TrendingUp,
  },
  { id: "clarity_and_communication", name: "Clarity & Communication", icon: MessageCircle },
  {
    id: "professional_and_ethical_standards",
    name: "Professional & Ethical Standards",
    icon: Shield,
  },
];

interface WeeklyBreakdownProps {
  program: Programme | null;
  assignments: Assignment[];
  submissions: Submission[];
  reviews: Review[];
}

export function WeeklyBreakdown({
  program,
  assignments,
  submissions,
  reviews,
}: WeeklyBreakdownProps) {
  const [selectedWeek, setSelectedWeek] = useState(program?.current_week ?? 1);
  const totalWeeks = program?.total_weeks ?? 4;

  const weekAssignments = assignments.filter((a) => a.week_number === selectedWeek);

  const weekData: Array<{ review: Review | null; isClosed: boolean }> = weekAssignments.map((a) => {
    const submission = submissions.find((s) => s.assignment_id === a.id);
    const review = submission ? reviews.find((r) => r.submission_id === submission.id) : null;
    return { review: review ?? null, isClosed: a.time_status === "closed" };
  });

  const reviewedCount = weekData.filter((d) => d.review).length;

  const categories = STANDARD_CRITERIA.map((criterion) => {
    const sectionScores: number[] = [];
    let maxScore = 20;

    for (const { review, isClosed } of weekData) {
      if (review?.sections) {
        const sectionData = review.sections[criterion.id];
        if (sectionData && typeof sectionData === "object" && "score" in sectionData) {
          sectionScores.push(sectionData.score);
          if (sectionData.max_score) maxScore = sectionData.max_score;
        }
      } else if (isClosed) {
        sectionScores.push(0);
      }
    }

    // Normalise to a 0-100 score across the week's assignments
    const score =
      sectionScores.length > 0
        ? Math.round(
            (sectionScores.reduce((sum, s) => sum + s, 0) / (sectionScores.length * maxScore)) * 100
          )
        : null;

    return { ...criterion, score };
  });

  const statusLine =
    reviewedCount === 0
      ? "Not yet reviewed"
      : reviewedCount === weekAssignments.length && weekAssignments.length > 1
        ? "Reviewed · both assignments"
        : "Reviewed · one assignment";

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-0">
        <div>
          <p className="text-[10px] font-bold text-content-muted uppercase tracking-[2px]">
            Criteria
          </p>
          <h3 className="text-xl font-extrabold text-card-foreground mt-0.5">Weekly Breakdown</h3>
        </div>
        <div className="relative">
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
            className="cursor-pointer appearance-none rounded-full border border-content-border bg-card py-2 pl-4 pr-9 text-xs font-bold uppercase tracking-[.12em] text-content-foreground transition-colors hover:border-brand/40"
          >
            {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((week) => (
              <option key={week} value={week}>
                Week {week}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-muted" />
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {categories.map((category) => {
            const Icon = category.icon;
            const hasScore = category.score !== null;

            return (
              <div
                key={category.id}
                className="rounded-xl border border-content-border bg-content-subtle p-5"
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`text-[28px] font-extrabold tracking-tight ${hasScore ? "text-card-foreground" : "text-content-muted"}`}
                  >
                    {hasScore ? category.score : "–"}
                  </span>
                  <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#E9F1E6]">
                    <Icon className="h-[19px] w-[19px] text-[#1E6F4F]" strokeWidth={1.7} />
                  </div>
                </div>
                <div className="mt-4 text-base font-bold text-card-foreground">{category.name}</div>
                <div className="mt-0.5 font-serif text-[13px] italic text-content-muted">
                  {hasScore ? statusLine : "Not yet reviewed"}
                </div>
                <div className="mt-3 h-[5px] overflow-hidden rounded-full bg-[#EFECDE]">
                  {hasScore && (
                    <div
                      className="h-full rounded-full bg-brand transition-[width] duration-500"
                      style={{ width: `${Math.min(category.score ?? 0, 100)}%` }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
